'use client';
/**
 * Lot G (17/08/2026) — « chaque analyse a des critères de performance ».
 *
 * VUE DÉRIVÉE, et c'est une décision : les critères de performance analytique
 * (SFBC, RICOS, CLIA) sont publiés PAR ANALYTE, jamais par examen. Un examen
 * n'a donc pas ses propres critères — il hérite de ceux des paramètres qu'il
 * mesure, et un panel (Ionogramme → Na, K, Cl) en hérite de plusieurs.
 *
 * Ce bloc ne fait que RAPPROCHER, en lecture seule. Rien n'est écrit, rien
 * n'est « adopté » : c'est la même doctrine que les intervalles de référence —
 * une table de société savante se consulte, et le laboratoire décide
 * séparément, par un geste explicite, de ce qu'il retient comme exigence. Le
 * jour où cette adoption sera voulue, elle se posera par une route dédiée, pas
 * par un affichage.
 *
 * Le rapprochement se fait sur le CODE de l'analyte (`analyteCode` des lignes
 * du référentiel) : c'est la seule clé commune aux deux registres.
 */
import React, { useEffect, useState } from 'react';
import { Tableau } from '@/shared/Tableau';
import { EmptyState, Skeleton, TitreRubrique } from '@/components/ui';
import { moteur, type FicheDetail } from './Registres';

type LigneCritere = {
  documentId: string; libelle: string; analyteCode: string | null;
  unite: string | null; notes: string | null; actif: boolean;
  sourceCode: string | null; sourceVersion: string | null;
} & Record<string, unknown>;

/** Les trois grandeurs qu'un biologiste compare d'un référentiel à l'autre.
 *  Chacune est publiée par niveau de concentration (bas / moyen / élevé). */
const GRANDEURS: { cle: string; libelle: string }[] = [
  { cle: 'reproductibilite', libelle: 'CV %' },
  { cle: 'justesse', libelle: 'Biais %' },
  { cle: 'inexactitude', libelle: 'ET %' },
];
const NIVEAUX = ['Bas', 'Moyen', 'Eleve'];

/** Condense « bas / moyen / élevé » en une seule valeur quand les trois sont
 *  identiques — c'est le cas de RICOS, qui publie une exigence unique. Sinon
 *  les trois sont montrées, séparées : les aplatir en une moyenne inventerait
 *  une valeur qu'aucune source ne publie. */
function valeur(l: LigneCritere, famille: string): string {
  const v = NIVEAUX.map((n) => l[`${famille}${n}`]).map((x) => (x === null || x === undefined ? null : Number(x)));
  const presentes = v.filter((x) => x !== null) as number[];
  if (!presentes.length) return '—';
  const toutes = new Set(presentes.map((x) => x.toFixed(2)));
  if (toutes.size === 1 && presentes.length === 3) return String(presentes[0]);
  return v.map((x) => (x === null ? '·' : String(x))).join(' / ');
}

/**
 * LES CRITÈRES DE PERFORMANCE D'UN SEUL PARAMÈTRE — extrait le 13/09/2026
 * pour être réutilisé tel quel dans le popup d'une carte CQI (« je veux
 * pouvoir cliquer sur la fiche du CQI et un pop-up me donne toutes les
 * infos », RQ) : la carte connaît son analyte, elle n'a pas à reconstruire ce
 * rapprochement, ni PerformanceFiche à l'exposer d'une façon différente pour
 * chaque appelant — même lecture, même table, deux endroits où l'ouvrir.
 */
export function CriteresPourAnalyte({ code, nom, lignes }: { code: string; nom: string; lignes: LigneCritere[] }) {
  const pour = lignes.filter((l) => (l.analyteCode || '').trim().toUpperCase() === code.trim().toUpperCase());
  return (
    <div>
      <TitreRubrique>{code} — {nom}</TitreRubrique>
      {!pour.length ? (
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          Aucun critère publié pour cet analyte dans les référentiels chargés (SFBC, RICOS, CLIA).
        </div>
      ) : (
        <Tableau
          /* Une clé PAR PARAMÈTRE : l'ordre des colonnes se garde par écran,
             et deux paramètres n'ont pas les mêmes exigences — partager la
             clé ferait dériver l'ordre de l'un sur l'autre. */
          id={`perf-${code}`}
          /* L6b (10/10/2026) : niveau SIMPLE — tri seulement, les grandeurs en nombres. */
          niveau="simple"
          lignes={pour}
          cleLigne={(l) => l.documentId}
          colonnes={[
            {
              cle: 'ref',
              libelle: 'Référentiel',
              valeur: (l) => l.sourceCode || '',
              rendu: (l) => (
                <>
                  <strong>{l.sourceCode || '—'}</strong>
                  {l.sourceVersion ? <span style={{ color: 'var(--text-muted)' }}> {l.sourceVersion}</span> : null}
                </>
              ),
            },
            ...GRANDEURS.map((g) => ({
              cle: g.cle,
              libelle: g.libelle,
              type: 'nombre' as const,
              /* Le tri porte sur le NOMBRE, pas sur la chaîne rendue : « 10 »
                 et « 9 » se rangent à l'endroit en numérique, à l'envers en
                 texte. `valeur()` rend la donnée brute, et `null` quand la
                 source ne publie rien pour ce niveau. */
              valeur: (l: typeof pour[number]) => {
                const v = valeur(l, g.cle);
                const n = Number(String(v).replace(',', '.').replace(/[^\d.-]/g, ''));
                return Number.isFinite(n) ? n : null;
              },
              rendu: (l: typeof pour[number]) => valeur(l, g.cle),
              style: { fontFamily: 'var(--font-mono)' },
            })),
            {
              cle: 'notes',
              libelle: 'Précisions',
              valeur: (l) => l.notes || l.unite || '',
              /* Les limites ABSOLUES (CLIA : Na ±4 mmol/L) n'ont pas de
                 pourcentage : elles vivent dans les notes, et les masquer
                 ferait croire à une absence d'exigence. */
              rendu: (l) => l.notes || (l.unite ? `unité ${l.unite}` : '—'),
              style: { color: 'var(--text-muted)' },
            },
          ]}
        />
      )}
    </div>
  );
}

/** Le CHARGEMENT des critères de performance, partagé par les deux appelants
 *  (`PerformanceFiche` et le popup d'une carte CQI) : une seule route, un
 *  seul filtre `actif`, jamais deux copies qui divergeraient. */
export function useCriteresPerformance(): { lignes: LigneCritere[] | null; erreur: string } {
  const [lignes, setLignes] = useState<LigneCritere[] | null>(null);
  const [erreur, setErreur] = useState('');
  useEffect(() => {
    let vivant = true;
    void moteur('ref-criteres').then((r) => {
      if (!vivant) return;
      if (!r.ok) { setErreur(r.message); setLignes([]); return; }
      setLignes(((r.data.lignes as LigneCritere[]) || []).filter((l) => l.actif !== false));
    });
    return () => { vivant = false; };
  }, []);
  return { lignes, erreur };
}

export function PerformanceFiche({ fiche }: { fiche: FicheDetail & { analytes?: { code: string; nom: string }[] } }) {
  const { lignes, erreur } = useCriteresPerformance();
  const analytes = fiche.analytes || [];

  if (!lignes) return <Skeleton rows={3} />;
  if (erreur) return <div style={{ fontSize: 12.5, color: 'var(--red-600)' }}>{erreur}</div>;

  if (!analytes.length) {
    return (
      <EmptyState titre="Aucun paramètre lié à cet examen"
        indice="Les critères de performance sont publiés par paramètre : liez d’abord les paramètres surveillés, onglet « Référentiel »." />
    );
  }

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5, maxWidth: '80ch' }}>
        Rapprochement en <strong>lecture seule</strong> : les critères sont publiés par analyte, cet examen en
        hérite. Rien n’est adopté ici — les valeurs que le laboratoire retient comme exigences se décident
        au registre des critères, comme pour les intervalles de référence.
      </p>

      {analytes.map((a) => <CriteresPourAnalyte key={a.code} code={a.code} nom={a.nom} lignes={lignes} />)}
      <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
        « bas / moyen / élevé » quand la source publie une exigence par niveau de concentration ; une valeur
        unique quand elle est la même aux trois niveaux. « · » = niveau non publié.
      </div>
    </div>
  );
}
